import { useState } from 'react';
import { Sparkles, Loader2, FileSearch, Tag, Copy, Check, X } from 'lucide-react';
import { aiHelperApi, type AIAction, type AIHelperResponse } from '../api/ai_helper';
import { Markdown } from '../lib/markdown';

interface Props {
  ticketId: string;
  onInsertToCompose?: (text: string) => void;
  onApplyClassify?: (c: { priority?: string; support_line?: number }) => void;
}

// Reply живёт в мочалке (рядом с сообщением клиента — AI: ответить).
// В сайдбаре оставляем только сквозные действия для всего тикета.
const ACTIONS: Array<{ id: AIAction; label: string; icon: any; hint: string }> = [
  { id: 'summary',  label: 'Резюме треда',    icon: FileSearch,
    hint: 'Краткий пересказ тикета для следующего агента.' },
  { id: 'classify', label: 'Классификация',   icon: Tag,
    hint: 'AI предлагает приоритет и линию поддержки на основе текста.' },
];

export default function TicketAIHelper({ ticketId, onInsertToCompose, onApplyClassify }: Props) {
  const [active, setActive] = useState<AIAction | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AIHelperResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const run = async (action: AIAction) => {
    setActive(action); setLoading(true); setErr(null); setResult(null);
    try {
      const r = await aiHelperApi.run(ticketId, action);
      setResult(r);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setLoading(false); }
  };

  const copy = () => {
    if (!result?.text) return;
    navigator.clipboard.writeText(result.text).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
        <Sparkles size={11} className="text-purple-400" /> AI-помощник
      </div>
      <div className="flex flex-col gap-1.5">
        {ACTIONS.map(a => {
          const Icon = a.icon;
          const isActive = active === a.id;
          return (
            <button key={a.id} onClick={() => run(a.id)} disabled={loading}
              title={a.hint}
              className={`text-xs flex items-center gap-1.5 px-2.5 py-1.5 rounded border transition ${
                isActive
                  ? 'bg-purple-900/30 border-purple-700 text-purple-200'
                  : 'bg-slate-950 border-slate-800 text-slate-300 hover:border-slate-600 hover:text-white'
              } disabled:opacity-50`}>
              {loading && isActive ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />}
              {a.label}
            </button>
          );
        })}
      </div>

      {err && (
        <div className="mt-2 bg-red-900/30 border border-red-800 text-red-300 px-2 py-1.5 rounded text-[11px]">{err}</div>
      )}

      {result && !err && (
        <div className="mt-3 bg-slate-950/60 border border-slate-800 rounded p-2.5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] text-slate-500 uppercase tracking-wider">
              {active === 'reply' ? 'Черновик' : active === 'summary' ? 'Резюме' : 'Классификация'}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={copy} className="text-slate-400 hover:text-white p-0.5" title="Скопировать">
                {copied ? <Check size={12} /> : <Copy size={12} />}
              </button>
              <button onClick={() => { setResult(null); setActive(null); }}
                className="text-slate-400 hover:text-white p-0.5" title="Закрыть">
                <X size={12} />
              </button>
            </div>
          </div>

          {result.action === 'classify' && result.classify ? (
            <div className="text-xs space-y-1.5">
              <div className="flex gap-2 flex-wrap">
                {result.classify.priority && (
                  <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-200 text-[11px] font-bold">
                    {result.classify.priority}
                  </span>
                )}
                {result.classify.support_line && (
                  <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-200 text-[11px]">
                    L{result.classify.support_line}
                  </span>
                )}
                {result.classify.category && (
                  <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-400 text-[11px]">
                    {result.classify.category}
                  </span>
                )}
              </div>
              {result.classify.reason && (
                <p className="text-[11px] text-slate-300 italic">{result.classify.reason}</p>
              )}
              {onApplyClassify && (
                <button onClick={() => onApplyClassify({
                  priority: result.classify?.priority,
                  support_line: result.classify?.support_line,
                })}
                  className="mt-1 text-[11px] bg-blue-600 hover:bg-blue-500 text-white px-2 py-1 rounded">
                  Применить к тикету
                </button>
              )}
            </div>
          ) : (
            <div className="text-[12px] text-slate-200 leading-relaxed max-h-[260px] overflow-y-auto">
              <Markdown source={result.text} />
            </div>
          )}

          {result.action === 'reply' && onInsertToCompose && result.text && (
            <button onClick={() => onInsertToCompose(result.text)}
              className="mt-2 w-full text-[11px] bg-blue-600 hover:bg-blue-500 text-white px-2 py-1 rounded">
              Вставить в форму ответа
            </button>
          )}

          {result.sources?.length > 0 && (
            <div className="mt-2 text-[10px] text-slate-500 border-t border-slate-800 pt-1.5">
              <div className="mb-0.5">Источники (RAG):</div>
              <ul className="space-y-0.5">
                {result.sources.map((s, i) => (
                  <li key={i} className="truncate"><span className="text-slate-400">[{(s.score*100|0)}%]</span> {s.filename}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
